/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import { f4 } from "../../math/float";
import { Vertex2D } from "../../math/vertex2d";
import { Mesh } from "../../mesh";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { RampData } from "./ramp-data";
import { RampHitGenerator } from "./ramp-hit-generator";
import { RampMeshGenerator } from "./ramp-mesh-generator";

/**
 * A ramp. It never moves.
 */
export class Ramp extends Item<RampData> implements IHittable {
  private readonly meshGenerator: RampMeshGenerator;
  private readonly hitGenerator: RampHitGenerator;
  private hits?: HitObject[];

  public constructor(data: RampData) {
    super(data);
    this.meshGenerator = new RampMeshGenerator(data);
    this.hitGenerator = new RampHitGenerator(data, this.meshGenerator);
  }

  public isCollidable(): boolean {
    return this.data.isCollidable;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "ramp", player.getEventSink());
    this.hits = this.hitGenerator.generateHitObjects(table, this.events);
  }

  public getHitShapes(): HitObject[] {
    return this.hits!;
  }

  public getSurfaceHeight(x: number, y: number, table: Table) {
    const vVertex = this.meshGenerator.getCentralCurve(table);

    const [vOut, iSeg] = Mesh.closestPointOnPolygon(vVertex, new Vertex2D(x, y), false);

    if (iSeg === -1) {
      return 0.0; // Object is not on ramp path
    }

    // Go through vertices (including iSeg itself) counting control points until iSeg
    let totalLength = 0.0;
    let startLength = 0.0;

    const cVertex = vVertex.length;
    for (let i2 = 1; i2 < cVertex; i2++) {
      const vDx = f4(vVertex[i2]!.x - vVertex[i2 - 1]!.x);
      const vDy = f4(vVertex[i2]!.y - vVertex[i2 - 1]!.y);
      const vLen = f4(Math.sqrt(f4(f4(vDx * vDx) + f4(vDy * vDy))));
      if (i2 <= iSeg) {
        startLength = f4(startLength + vLen);
      }
      totalLength = f4(totalLength + vLen);
    }

    const dx = f4(vOut.x - vVertex[iSeg]!.x);
    const dy = f4(vOut.y - vVertex[iSeg]!.y);
    const len = f4(Math.sqrt(f4(f4(dx * dx) + f4(dy * dy))));
    startLength = f4(startLength + len); // Add the distance the object is between the two closest polyline segments.  Matters mostly for straight edges. Z does not respect that yet!

    const topHeight = f4(this.data.heightTop + table.getTableHeight());
    const bottomHeight = f4(this.data.heightBottom + table.getTableHeight());

    return f4(
      f4(vVertex[iSeg]!.z + f4(f4(startLength / totalLength) * f4(topHeight - bottomHeight))) +
        bottomHeight,
    );
  }
}
